import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Tooltip,
  Input,
  Menu,
  Dropdown,
  Avatar,
  Badge,
  List,
  MenuProps,
  Tag,
  notification,
  Divider,
  Progress,
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
  StockOutlined,
  CloseCircleOutlined,
  RobotOutlined,
  SecurityScanOutlined,
  QuestionCircleOutlined,
  LogoutOutlined,
  MenuOutlined,
  SettingOutlined,
  NotificationOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  GroupOutlined,
  CustomerServiceOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { resetProfile, setNotifications } from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import styles from "./Layout.module.css";
import "./Layout.css";
import AuthService from "../../services/auth/AuthService";
import { BATCH_PROCESSING_MAP_STATUS, BATCH_PROCESSING_MAP_STATUS_REVERSE, STEP_STATUS } from "../../config/consts/extraction";
import { Link, useNavigate } from "react-router-dom";
import NotificationService from "../../services/notifications/NotificationsService";
import BatchProcessingService from "../../services/batchprocessing/BatchProcessingService";
import moment from "moment";
import { ROUTES } from "../../config/consts/routes";

const { Search } = Input;
type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: "group"
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    type,
  } as MenuItem;
}

const reportMenuDashboard: MenuItem[] = [
  getItem(<div>General Overview</div>, "1", <GroupOutlined />),
  getItem(<div>Custom Queries</div>, "2", <CustomerServiceOutlined />),
];

const reportMenuReports: MenuItem[] = [
  getItem(<div>Load File Issues</div>, "1", <GroupOutlined />),
];

const reportMenuDocumentation: MenuItem[] = [
  getItem(<div>Release Notes</div>, "1", <GroupOutlined />),
];

export const Head = ({ activeProcesses }: { activeProcesses: any }) => {
  const [showButton, setShowButton] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [notificationsDropdownOpen, setNotificationsDropdownOpen] =
    useState<boolean>(false);
  const [processingDropdownOpen, setProcessingDropdownOpen] =
    useState<boolean>(false);
  const refreshNotificationsRef = useRef(false);
  const REFRESH_NOTIFICATIONS_AFTER_TIME_IN_SECONDS = 3;

  const [batchProcessingItems, setBatchProcessingItems] = useState<Array<any>>(
    []
  );
  const [unreadNotifications, setUnreadNotifications] = useState<any>([]);
  const [numberOfUnreadNotifications, setNumberOfUnreadNotifications] = useState<any>([]);

  const notificationDropdownOpenRef = useRef(notificationsDropdownOpen);
  notificationDropdownOpenRef.current = notificationsDropdownOpen;

  const [numberOfActiveProcesses, setNumberOfActiveProcesses] = useState(0);

  const { profile, notifications } = useSelector((state: any) => ({
    profile: state.profile,
    notifications: state.notifications,
  }));

  const [notificationsToDispatch, setNotificationsToDispatch] = useState([]);

  useEffect(() => {
    setBatchProcessingItems(activeProcesses.queueTasks);
    setNumberOfActiveProcesses(activeProcesses.numberOfActiveTasks);
  }, [activeProcesses]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogoutClick = () => {
    if (profile?.msal?.logout) {
      profile.msal.logout();
    }
    dispatch(resetProfile());
  };

  const handleMyAccountClick = () => {
    navigate(ROUTES.administration.account.route);
  };

  const handleUserManagementClick = () => {
    navigate(ROUTES.administration.userManagement.route);
  };

  useEffect(() => {
    if (notifications) {
      var unreadNotifications = notifications
        .sort(function (a: any, b: any) {
          if (a.id < b.id) {
            return -1;
          }
          if (a.id > b.id) {
            return 1;
          }
          return 0;
        })
        .filter((x: any) => !x.seen);
      setUnreadNotifications(unreadNotifications);
      setNumberOfUnreadNotifications(unreadNotifications?.length)
    }
  }, [notifications]);

  useEffect(() => {
    NotificationService.getAll().then((response: any) => {
      dispatch(setNotifications(response.data));
    });

    BatchProcessingService.getAll(true).then((response: any) => {
      const data = response.data.queueTasks.map((x: any) => ({
        ...x,
        executionTime: x.executionTime.split(".")[0],
        // runtime: x.runtime.split(".")[0].split(":")[0] + ":" + x.runtime.split(".")[0].split(":")[1] + ":" + x.runtime.split(".")[0].split(":")[2]
        steps: x.steps.map((y: any) => ({
          ...y,
          executionTime: y.executionTime.split(".")[0],
          status: BATCH_PROCESSING_MAP_STATUS(y.status),
        })),
        status: BATCH_PROCESSING_MAP_STATUS(x.status),
      }));

      setBatchProcessingItems(data);
    });
  }, []);

  useEffect(() => {
    refreshNotificationsRef.current = notificationsDropdownOpen;
    if (notificationsDropdownOpen) {
      setTimeout(() => {
        setNumberOfUnreadNotifications((prev) => prev - 5 > 0 ? prev - 5 : 0);
        if (refreshNotificationsRef.current) {
          var filteredNotifications = unreadNotifications
            .slice(0, 5)
            .map((n) => ({
              notificationId: n.id,
              userId: profile.id,
            }));

          NotificationService.createUserNotifications(
            filteredNotifications
          ).then(() => {
            NotificationService.getAll().then((response: any) => {
              setNotificationsToDispatch(response.data);
            });
          });
        }
      }, REFRESH_NOTIFICATIONS_AFTER_TIME_IN_SECONDS * 1000);
    }
  }, [notificationsDropdownOpen]);

  useEffect(() => {
    if (!notificationsDropdownOpen) {
      if (!!notificationsToDispatch.length) {
        dispatch(setNotifications(notificationsToDispatch));
        setNotificationsToDispatch([]);
      }
    }
  }, [notificationsDropdownOpen, notificationsToDispatch]);

  const triggerProcessingDropdown = () => {
    setProcessingDropdownOpen(true);
  };

  const menu = (
    <Menu
      style={{ padding: "10px" }}
      selectable
      defaultSelectedKeys={[""]}
      items={[
        {
          key: "1",
          label: "My Account",
          icon: <UserOutlined />,
          onClick: handleMyAccountClick,
        },
        {
          key: "2",
          label: "User Management",
          icon: <SecurityScanOutlined />,
          onClick: handleUserManagementClick,
        },
        // {
        //   key: "3",
        //   label: "Help",
        //   icon: <SettingOutlined />,
        // },
        {
          key: "4",
          label: "Logout",
          icon: <LogoutOutlined />,
          onClick: handleLogoutClick,
        },
      ]}
    />
  );

  const processingDropdown = (
    <div className={styles.processing_wrap}>
      {batchProcessingItems?.map((x: any) => (
        <div className={styles.item_wrap}>
          <div className={styles.processing_wrap_title}>{x.name}</div>
          <div>
            <Progress
              showInfo={false}
              percent={100}
              steps={x.steps.length}
              strokeColor={x.steps.map((y: any) => {
                var status = !!Number(y.status) ? Number(y.status) : BATCH_PROCESSING_MAP_STATUS_REVERSE(y.status);
                switch (status) {
                  case STEP_STATUS.Success:
                    return "var(--accent)"
                  case STEP_STATUS.Fail:
                    return "#f5222d"
                  case STEP_STATUS.Running:
                    return "#2F54EB"
                  default:
                    return "#F5F5F5";
                }
              })}
            />
            <span style={{ color: "#8C8C8C" }}>
              {
                x.steps.filter(
                  (y: any) => {
                    var status = !!Number(y.status) ? Number(y.status) : BATCH_PROCESSING_MAP_STATUS_REVERSE(y.status);
                    return (status === STEP_STATUS.Success ||
                      y.status === STEP_STATUS.Fail ||
                      y.status === STEP_STATUS.Running)
                  }
                ).length
              }
              /{x.steps.length}
            </span>
            <Tag>{moment.utc(x.executionTime.split(".")[0]).local().format("YYYY-MM-DD HH:mm:ss")}</Tag>
          </div>
        </div>
      ))}
      <div className={styles.reviewAll_button}>
        <Button type="text" href={ROUTES.batchProcessing.processOverview.route}>
          Review All
        </Button>
      </div>
    </div>
  );

  const notificationsDropdown = (
    <div
      className={styles.notification_wrap}
      style={{ border: "1px solid #efefef" }}
    >
      <div className={styles.title_container}>
        <span style={{ fontSize: "16px" }}>Notifications</span>
        <span style={{ fontSize: "12px" }}>
          {numberOfUnreadNotifications} unread
        </span>
      </div>
      {unreadNotifications.slice(0, 5).map((x: any) => (
        <div className={styles.notification_container}>
          {x.type.toLowerCase() === "error" && (
            <>
              {" "}
              <Avatar
                style={{ backgroundColor: "#FFF1F0" }}
                icon={
                  <ExclamationCircleOutlined style={{ color: "#FF4D4F" }} />
                }
                shape="square"
              />
            </>
          )}
          {(x.type.toLowerCase() === "notification" ||
            x.type.toLowerCase() === "generic") && (
              <>
                {" "}
                <Avatar
                  icon={<InfoCircleOutlined style={{ color: "#1890FF" }} />}
                  shape="square"
                  style={{ backgroundColor: "#E6F7FF" }}
                />
              </>
            )}
          {x.type.toLowerCase() === "warning" && (
            <>
              {" "}
              <Avatar
                style={{ backgroundColor: "#FFFBE6" }}
                icon={<WarningOutlined style={{ color: "#FAAD14" }} />}
                shape="square"
              />
            </>
          )}

          <div className={styles.text_container}>
            <span style={{ fontWeight: "bold", color: "#1E1E85" }}>{ }</span>
            <span style={{ color: "#8C8C8C" }}>
              {x.description.length > 50
                ? x.description.slice(0, 50) + "..."
                : x.description}
            </span>
          </div>
          <div className={styles.tagContainer}>
            {/* <Tag>{getTimePassed(x.dateCreated)}</Tag> */}
          </div>
        </div>
      ))}
      <div className={styles.notification_button}>
        <Button
          type="text"
          icon={<ArrowRightOutlined />}
          href={ROUTES.notifications.route}
        >
          View All
        </Button>
      </div>
    </div>
  );

  const reportDropdown = (
    <div className={styles.reportDropdown}>
      <div className={styles.dashboardWrap}>
        <p>Dashboard</p>
        <div>
          <GroupOutlined />
          <p>General Overview</p>
        </div>
        <div onClick={() => navigate(ROUTES.report.customQueries.route)}>
          <CustomerServiceOutlined />
          <p>Custom Queries</p>
        </div>
      </div>
      <Divider type="vertical" style={{ height: "auto" }} />
      <div className={styles.dashboardWrap}>
        <p>Reports</p>
        <div>
          <>&#8226;</>
          <p>Load File Issues</p>
        </div>
        <div onClick={() => navigate(ROUTES.report.unfinishedMappings.route)}>
          <>&#8226;</>
          <p>Unfinished Mappings</p>
        </div>
        <div onClick={() => navigate(ROUTES.report.analytics.route)}>
          <>&#8226;</>
          <p>Analytics</p>
        </div>
      </div>
      <Divider type="vertical" style={{ height: "auto" }} />
      <div className={styles.dashboardWrap}>
        <p>Documentation</p>
        <div onClick={() => navigate(ROUTES.report.releaseNotes.route)}>
          <>&#8226;</>
          <p>Release Notes</p>
        </div>
        <div onClick={() => navigate(ROUTES.report.manuals.route)}>
          <>&#8226;</>
          <p>Manuals</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.wrap}>
      <div className={styles.head_left}>
        <div className="borderActiveButton">
          <Dropdown
            overlay={reportDropdown}
            placement="bottomLeft"
            arrow
            // trigger={["click"]}
            overlayStyle={{ top: "44px" }}
          >
            <Button
              shape="default"
              icon={<StockOutlined />}
              style={{
                backgroundColor: `var(--white)`,
                color: `var(--primary)`,
                border: "none",
                boxShadow: "none",
                paddingLeft: 0,
                paddingBottom: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              Reports & Diagnostics
            </Button>
          </Dropdown>
        </div>
        {/* <div className="borderActiveButton">
          <Button
            // icon={<StockOutlined />}
            style={{
              backgroundColor: `var(--white)`,
              color: `var(--primary)`,
              border: "none",
              boxShadow: "none",
            }}
          >
            Additional button
          </Button>
        </div> */}
      </div>

      <div className={styles.head_right}>
        <Dropdown
          className="processing_dropdown"
          overlay={processingDropdown}
          placement="bottomRight"
          trigger={["click"]}
        >
          <Badge count={numberOfActiveProcesses}>
            <Button
              style={{
                background: "#1E1E85",
                color: "white",
                border: "none",
              }}
              icon={<SyncOutlined />}
              onClick={() => triggerProcessingDropdown()}
            ></Button>
          </Badge>
        </Dropdown>

        <Dropdown
          className="notification_dropdown"
          overlay={notificationsDropdown}
          placement="bottomRight"
          trigger={["click"]}
          onVisibleChange={setNotificationsDropdownOpen}
        >
          {notifications?.filter((x: any) => !x.seen).length > 0 ? (
            <Badge dot>
              <Button
                style={{
                  background: "#1E1E85",
                  color: "white",
                  border: "none",
                }}
                icon={<NotificationOutlined />}
              ></Button>
            </Badge>
          ) : (
            <Button
              style={{
                background: "#1E1E85",
                color: "white",
                border: "none",
              }}
              icon={<NotificationOutlined />}
            ></Button>
          )}
        </Dropdown>

        <Dropdown
          overlay={menu}
          placement="bottomRight"
          arrow
          trigger={["click"]}
        >
          <div className="backgroundActiveButton">
            <Button
              shape="default"
              icon={<MenuOutlined />}
              style={{
                color: `var(--primary)`,
                border: "none",
                boxShadow: "none",
              }}
            >
              {profile.displayName ?? ""}
            </Button>
          </div>
        </Dropdown>
      </div>
    </div>
  );
};
